import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiX, FiCheckCircle, FiClock, FiAward } = FiIcons;

const QuoteModal = ({ isOpen, onClose }) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      
      const script = document.createElement('script');
      script.src = "https://cdn.deftform.com/embed.js";
      script.async = true;
      document.body.appendChild(script);

      return () => {
        document.body.style.overflow = 'unset';
        if (document.body.contains(script)) {
          document.body.removeChild(script);
        }
      };
    }
  }, [isOpen]);

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-0 md:p-6">
          {/* Enhanced Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-primary/80 backdrop-blur-sm"
          />

          {/* Sleek Modal Container */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 30 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="relative bg-white w-full max-w-5xl md:rounded-lg shadow-2xl overflow-hidden flex flex-col md:flex-row max-h-[100vh] md:max-h-[90vh]"
          >
            {/* Left Side: Brand Experience (Desktop Only) */}
            <div className="hidden md:flex md:w-2/5 bg-primary relative overflow-hidden flex-col justify-between p-10">
              <div className="absolute inset-0 opacity-20">
                <img 
                  src="https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80" 
                  alt="Interior Detail" 
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-primary via-primary/80 to-primary"></div>
              </div>

              <div className="relative z-10">
                <div className="text-2xl font-serif font-bold text-white mb-8">
                  Arvind<span className="text-accent">Interiors</span>
                </div>
                <h3 className="text-3xl font-serif text-white leading-tight mb-4">
                  Let's Create Your <br />
                  <span className="text-accent underline decoration-accent/30 underline-offset-8">Perfect Space</span>
                </h3>
                <p className="text-gray-400 text-sm leading-relaxed max-w-xs">
                  Fill out the form to help us understand your vision. Our design consultants will reach out within 24 hours.
                </p>
              </div>

              <div className="relative z-10 space-y-6">
                <div className="flex items-center gap-4 group">
                  <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center border border-accent/20 group-hover:bg-accent/20 transition-colors">
                    <SafeIcon icon={FiClock} className="text-accent w-5 h-5" />
                  </div>
                  <span className="text-white/80 text-sm font-medium tracking-wide">24h Response Time</span>
                </div>
                <div className="flex items-center gap-4 group">
                  <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center border border-accent/20 group-hover:bg-accent/20 transition-colors">
                    <SafeIcon icon={FiCheckCircle} className="text-accent w-5 h-5" />
                  </div>
                  <span className="text-white/80 text-sm font-medium tracking-wide">Custom Design Plan</span>
                </div>
                <div className="flex items-center gap-4 group">
                  <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center border border-accent/20 group-hover:bg-accent/20 transition-colors">
                    <SafeIcon icon={FiAward} className="text-accent w-5 h-5" />
                  </div>
                  <span className="text-white/80 text-sm font-medium tracking-wide">Premium Execution</span>
                </div>
              </div>
            </div>

            {/* Right Side: Form Content */}
            <div className="flex-1 flex flex-col bg-white overflow-hidden">
              {/* Header for Mobile/Desktop */}
              <div className="flex justify-between items-center px-6 py-6 border-b border-gray-50">
                <div className="md:hidden">
                  <span className="text-lg font-serif font-bold text-primary">Arvind<span className="text-accent">Interiors</span></span>
                </div>
                <div className="hidden md:block">
                  <h4 className="text-sm uppercase tracking-[0.2em] font-bold text-primary/40">Request a Consultation</h4>
                </div>
                <button 
                  onClick={onClose}
                  className="p-2 hover:bg-gray-100 rounded-full transition-all duration-300 group hover:rotate-90"
                  aria-label="Close modal"
                >
                  <SafeIcon icon={FiX} className="w-6 h-6 text-primary group-hover:text-accent" />
                </button>
              </div>

              {/* Form Area */}
              <div className="flex-1 overflow-y-auto custom-scrollbar">
                <div className="px-6 py-8 md:px-10 md:py-12">
                  <div className="md:hidden mb-8">
                    <h3 className="text-2xl font-serif font-bold text-primary mb-2">Get Your Quote</h3>
                    <p className="text-gray-500 text-sm">Tell us about your project requirements.</p>
                  </div>

                  <div 
                    className="deftform min-h-[500px]" 
                    data-form-id="c8d2a88b-0042-4360-8de0-15cf8b698cf2" 
                    data-form-width="100%" 
                    data-form-align="center" 
                    data-form-auto-height="1"
                  >
                    {/* Professional Loader */}
                    <div className="flex flex-col items-center justify-center py-32 space-y-4">
                      <div className="relative">
                        <div className="w-12 h-12 border-2 border-accent/20 rounded-full"></div>
                        <div className="w-12 h-12 border-t-2 border-accent rounded-full animate-spin absolute top-0 left-0"></div>
                      </div>
                      <div className="flex flex-col items-center">
                        <p className="text-primary font-serif font-semibold tracking-wide">Preparing Your Form</p>
                        <p className="text-xs text-gray-400 uppercase tracking-widest mt-1">Securing Connection...</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer Note */}
              <div className="px-10 py-4 bg-surface border-t border-gray-50 text-center">
                <p className="text-[10px] text-gray-400 uppercase tracking-widest">
                  Your data is protected and will never be shared with third parties.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default QuoteModal;